class AdvancedSearch extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitButton = this.querySelector('button[type="submit"]');
    this.zoneSelector = this.querySelector('#zone-selector');
    this.besoinSelector = this.querySelector('#besoin-selector');
    this.productTypeSelector = this.querySelector('#product-type-selector');
    this.collectionHandle = this.dataset.collection;

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }
  }

  async handleSubmit(event) {
    event.preventDefault();

    // Validate that at least one filter is selected
    if (!this.hasAnyFilterSelected()) {
      // No filters selected, show a message or handle as needed
      console.warn('No filters selected');
      return;
    }

    if (this.submitButton) {
      this.submitButton.querySelector('.loading__spinner').classList.remove('hidden');
      this.submitButton.setAttribute('disabled', 'true');
      this.submitButton.classList.add('loading');
    }

    try {
      const searchResults = await this.performSearch();
      this.handleSearchResults(searchResults);
    } catch (error) {
      console.error('Search error:', error);
      // Fallback to collection page if search fails
      this.redirectToCollectionWithFilters();
    } finally {
      if (this.submitButton) {
        this.submitButton.querySelector('.loading__spinner').classList.add('hidden');
        this.submitButton.removeAttribute('disabled');
        this.submitButton.classList.remove('loading');
      }
    }
  }

  hasAnyFilterSelected() {
    return (
      (this.zoneSelector && this.zoneSelector.value) ||
      (this.besoinSelector && this.besoinSelector.value) ||
      (this.productTypeSelector && this.productTypeSelector.value)
    );
  }

  async performSearch() {
    // Build the filter parameters
    const filterParams = this.buildFilterParams();

    // Create the URL for the collection with filters and sections parameter
    const url = `/collections/${this.collectionHandle}?sections=search-advanced-results${filterParams !== '' ? '&' + filterParams : ''}`;

    try {
      // Fetch the HTML response
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      // Get the response data
      const responseData = await response.json();

      // Extract JSON from the script tag
      const jsonData = this.extractJsonFromHtml(responseData['search-advanced-results']);

      return jsonData;
    } catch (error) {
      console.error('Error fetching results:', error);
      throw error;
    }
  }

  extractJsonFromHtml(html) {
    try {
      // Create a parser to extract the script content
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the script tag with the JSON data
      const scriptTag = doc.querySelector('script#search-advanced-results');

      if (scriptTag && scriptTag.textContent) {
        // Parse the JSON from the script content
        try {
          const jsonData = JSON.parse(scriptTag.textContent);
          return jsonData;
        } catch (parseError) {
          console.error('Error parsing JSON:', parseError);
          return { resources: { results: { products: [] } } };
        }
      } else {
        console.error('Script tag with JSON data not found');
        return { resources: { results: { products: [] } } };
      }
    } catch (error) {
      console.error('Error extracting JSON from HTML:', error);
      return { resources: { results: { products: [] } } };
    }
  }

  buildFilterParams() {
    // Array to store param entries
    const paramEntries = [];

    // Helper function to format the parameter
    const formatParam = (key, value) => {
      // Encode the key but handle spaces in value manually
      const encodedKey = encodeURIComponent(key);
      // For the value, we encode but then replace encoded spaces (%20) with + signs
      const encodedValue = encodeURIComponent(value).replace(/%20/g, '+');
      return `${encodedKey}=${encodedValue}`;
    };

    if (this.zoneSelector && this.zoneSelector.value) {
      const filterName = this.zoneSelector.dataset.filter || 'filter.p.m.details.zone';
      paramEntries.push(formatParam(filterName, this.zoneSelector.value));
    }

    if (this.besoinSelector && this.besoinSelector.value) {
      const filterName = this.besoinSelector.dataset.filter || 'filter.p.m.details.besoin';
      paramEntries.push(formatParam(filterName, this.besoinSelector.value));
    }

    if (this.productTypeSelector && this.productTypeSelector.value) {
      paramEntries.push(formatParam('filter.p.product_type', this.productTypeSelector.value));
    }

    const queryString = paramEntries.join('&');
    console.log('Filter parameters:', queryString, this.productTypeSelector ? this.productTypeSelector.value : '');

    return queryString;
  }

  handleSearchResults(results) {
    if (!results || !results.resources || !results.resources.results) {
      console.error('Invalid search results format:', results);
      this.redirectToCollectionWithFilters();
      return;
    }

    const products = results.resources.results.products || [];

    if (products.length === 0) {
      // No products found, redirect to collection page
      this.redirectToCollectionWithFilters();
      return;
    }

    if (products.length === 1) {
      // If only one product is found, redirect to that product
      const product = products[0];
      window.location.href = `/products/${product.handle || product.id}`;
    } else {
      // Otherwise, redirect to collection page with filters
      this.redirectToCollectionWithFilters();
    }
  }

  redirectToCollectionWithFilters() {
    // Determine which collection to use
    let collectionHandle = this.collectionHandle;

    // If no collection handle is available, use 'all'
    if (!collectionHandle) {
      collectionHandle = 'all';
    }

    // Build the URL with filters
    const filterParams = this.buildFilterParams();
    const url = `/collections/${collectionHandle}?${filterParams}`;

    // Redirect to the collection page
    window.location.href = url;
  }
}

customElements.define('advanced-search', AdvancedSearch);
